import type {
  InspectionStatus,
  ItemView,
  ReturnUnit,
  UserRef,
  Verdict,
} from "@/api/okk_receiving";
import { fmtDate } from "@/lib/format";
import type { Tone } from "@/lib/order-journal";

/** Подписи и правила раздела «ОКК: входной контроль» — одно место на экраны. */

export const STATUS_INFO: Record<InspectionStatus, { text: string; tone: Tone }> = {
  new: { text: "новая", tone: "warn" },
  in_progress: { text: "в работе", tone: "warn" },
  testing: { text: "на тесте", tone: "muted" },
  done: { text: "готово", tone: "ok" },
};

export function statusInfo(status: string): { text: string; tone: Tone } {
  // Неизвестный статус — показываем как есть, а не «новая»: старая вкладка
  // после выката не должна врать о состоянии проверки.
  return STATUS_INFO[status as InspectionStatus] ?? { text: status, tone: "muted" };
}

/** Порядок кнопок вердикта; `not_required` — только у `unassigned`. */
export const VERDICTS: Verdict[] = [
  "in_work",
  "conditional",
  "testing",
  "quarantine",
  "return",
  "not_required",
];

export const VERDICT_INFO: Record<Verdict, { button: string; short: string; tone: Tone }> = {
  in_work: { button: "В работу", short: "в работу", tone: "ok" },
  conditional: { button: "С условием", short: "с условием", tone: "warn" },
  testing: { button: "На тесте", short: "на тесте", tone: "muted" },
  quarantine: { button: "Карантин", short: "карантин", tone: "bad" },
  return: { button: "Возврат", short: "возврат", tone: "bad" },
  not_required: { button: "Не требует проверки", short: "не требует", tone: "muted" },
};

export function verdictInfo(v: string): { button: string; short: string; tone: Tone } {
  return VERDICT_INFO[v as Verdict] ?? { button: v, short: v, tone: "muted" };
}

/** Как `VERDICTS_NEED_NOTE` бэкенда: без комментария сервер ответит 422. */
export const VERDICTS_NEED_NOTE: ReadonlySet<Verdict> = new Set<Verdict>([
  "conditional",
  "quarantine",
  "return",
]);

export const RETURN_UNIT_TITLE: Record<ReturnUnit, string> = { pcs: "шт", places: "мест" };

/** Позиция без правила маршрута — «группа не настроена». */
export const TARGET_UNASSIGNED = "unassigned";

export function verdictsFor(item: Pick<ItemView, "target">): Verdict[] {
  return VERDICTS.filter((v) => v !== "not_required" || item.target === TARGET_UNASSIGNED);
}

/** Кто: ФИО из 1С, если учётка привязана, иначе логин портала. */
export function authorName(u: UserRef | null | undefined): string {
  if (!u) return "—";
  return u.one_c_name || u.username;
}

/**
 * Число с клавиатуры: запятая = точка, пусто → `null`, мусор → `NaN`.
 * Отдаём строку с точкой (на сервере Decimal) и число для проверок.
 */
export function parseDecimal(raw: string): { value: number; text: string } | null {
  const text = raw.trim().replace(/\s+/g, "").replace(",", ".");
  if (!text) return null;
  if (!/^\d+(\.\d+)?$/.test(text)) return { value: Number.NaN, text };
  return { value: Number(text), text };
}

const MOSCOW_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Moscow",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Сегодня по Москве в ISO `yyyy-mm-dd`: сервер сверяет срок теста с МСК-датой
 * (`test_testing_date_in_the_past_is_refused`), а часовой пояс устройства может
 * быть любым — отказ «срок прошёл» должен совпадать с серверным.
 */
export function todayIso(now: Date = new Date()): string {
  const parts = Object.fromEntries(MOSCOW_DATE.formatToParts(now).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Вердикт «не требует проверки» разрешён только позиции без правила маршрута
 * (`unassigned`). Если позиция потом попала на ОКК (правило изменилось), старый
 * вердикт неуместен — сервер на завершении ответит 409.
 */
export function isNotRequiredOnOkk(item: Pick<ItemView, "verdict" | "target">): boolean {
  return item.verdict === "not_required" && item.target !== TARGET_UNASSIGNED;
}

/**
 * Почему «Завершить проверку» недоступна — те же условия, что у
 * `service.complete` (сервер всё равно проверит и ответит 409 с текстом).
 * Пустой список — можно завершать.
 */
export function completeBlockers(items: ItemView[]): string[] {
  const active = items.filter((i) => !i.removed_from_delivery);
  const noVerdict = active.filter((i) => i.verdict === null).length;
  const testing = active.filter((i) => i.verdict === "testing").length;
  const misplaced = active.filter(isNotRequiredOnOkk).length;
  const out: string[] = [];
  if (noVerdict) out.push(`без вердикта: ${noVerdict}`);
  if (testing) out.push(`на тесте: ${testing}`);
  if (misplaced) out.push(`позиция стала ОКК, а вердикт «не требует»: ${misplaced}`);
  return out;
}

/** УПД: номер и дата; приход без документов — так и пишем. */
export function updText(d: {
  incoming_number: string;
  incoming_date: string | null;
  upd_missing: boolean;
}): string {
  if (d.upd_missing) return "без УПД";
  const n = d.incoming_number ? `УПД ${d.incoming_number}` : "УПД —";
  return d.incoming_date ? `${n} от ${fmtDate(d.incoming_date)}` : n;
}
